import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useErrorText } from '../../shared/api/useErrorText';
import { useForm, Resolver } from 'react-hook-form';
import { z } from 'zod';
import { SignUpSchema } from '@myscaw/shared';
import { useAuth } from '../../shared/auth/AuthContext';
import { TextField, Button } from '../../shared/ui';
import { LanguageSwitcher } from '../../shared/LanguageSwitcher';

type SignUpFormValues = z.infer<typeof SignUpSchema> & {
  confirmPassword: string;
};

function getPasswordStrength(password: string): {
  score: number;
  labelKey: string;
  colorClass: string;
  barColor: string;
} {
  if (!password) {
    return { score: 0, labelKey: '', colorClass: '', barColor: 'bg-surface-container-high' };
  }
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password) || password.length >= 12) score++;

  if (score <= 1) {
    return {
      score: 1,
      labelKey: 'auth.strengthWeak',
      colorClass: 'text-error',
      barColor: 'bg-error',
    };
  }
  if (score <= 3) {
    return {
      score: 2,
      labelKey: 'auth.strengthMedium',
      colorClass: 'text-amber-500',
      barColor: 'bg-amber-500',
    };
  }
  return {
    score: 3,
    labelKey: 'auth.strengthStrong',
    colorClass: 'text-emerald-500',
    barColor: 'bg-emerald-500',
  };
}

export function SignUpPage() {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const honeypotRef = useRef<HTMLInputElement>(null);

  const translateValidationError = (issue: z.ZodIssue): string => {
    const field = String(issue.path[0] ?? '');
    const msg = issue.message;

    if (field === 'email') {
      if (msg.includes('ISCAE') || msg.includes('groupeiscae')) {
        return t('auth.errors.emailIscae');
      }
      if (issue.code === 'invalid_string' || msg.toLowerCase().includes('email')) {
        return t('auth.errors.emailInvalid');
      }
      return t('auth.errors.emailRequired');
    }

    if (field === 'firstName') {
      return t('auth.firstNameRequired');
    }

    if (field === 'lastName') {
      return t('auth.lastNameRequired');
    }

    if (field === 'password') {
      if (msg.includes('at least 8') || issue.code === 'too_small') {
        return t('auth.errors.passwordMinLength');
      }
      if (msg.includes('at most 128') || issue.code === 'too_big') {
        return t('auth.errors.passwordMaxLength');
      }
      if (msg.includes('uppercase')) {
        return t('auth.errors.passwordUppercase');
      }
      if (msg.includes('lowercase')) {
        return t('auth.errors.passwordLowercase');
      }
      if (msg.includes('digit') || msg.includes('chiffre')) {
        return t('auth.errors.passwordDigit');
      }
      return t('auth.errors.passwordRequired');
    }

    if (field === 'gender') {
      return t('auth.errors.genderRequired');
    }

    if (field === 'phone') {
      if (msg.includes('invalide') || msg.includes('invalid') || msg.includes('0612345678')) {
        return t('auth.errors.phoneInvalid');
      }
      return t('auth.errors.phoneRequired');
    }

    if (field === 'acceptsPrivacyPolicy') {
      return t('auth.privacyPolicyRequired');
    }

    return msg;
  };

  const signUpResolver: Resolver<SignUpFormValues> = async (values) => {
    const result = SignUpSchema.safeParse(values);
    const validationErrors: Record<string, { type: string; message: string }> = {};

    if (!result.success) {
      for (const issue of result.error.issues) {
        const key = String(issue.path[0] ?? '');
        if (key && !validationErrors[key]) {
          validationErrors[key] = {
            type: 'validation',
            message: translateValidationError(issue),
          };
        }
      }
    }

    if (!values.confirmPassword) {
      validationErrors.confirmPassword = {
        type: 'validation',
        message: t('auth.confirmPasswordRequired'),
      };
    } else if (values.password !== values.confirmPassword) {
      validationErrors.confirmPassword = {
        type: 'validation',
        message: t('auth.passwordMismatch'),
      };
    }

    if (!result.success || Object.keys(validationErrors).length > 0) {
      return { values: {}, errors: validationErrors };
    }

    return {
      values: { ...result.data, confirmPassword: values.confirmPassword } as SignUpFormValues,
      errors: {},
    };
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormValues>({ resolver: signUpResolver });

  const watchedPassword = watch('password') || '';
  const strength = getPasswordStrength(watchedPassword);

  const onSubmit = async (data: SignUpFormValues) => {
    // Honeypot (P1): a bot that fills every field it sees trips here and the
    // submission stops without ever touching the API - no error shown, because
    // showing one only teaches the bot that `website` matters. The matching
    // `website` field on SignUpSchema makes the server answer 422 to a bot that
    // posts directly instead.
    if (honeypotRef.current?.value) return;
    setError(null);
    setSubmitting(true);
    try {
      const { confirmPassword: _, ...payload } = data;
      await signUp(payload);
      navigate(`/verify?email=${encodeURIComponent(data.email)}`);
    } catch (e: any) {
      setError(errorText(e, 'auth.signUpFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-surface font-body-md text-on-surface flex items-center justify-center min-h-screen">
      <main className="w-full max-w-md px-6 py-12">
        <div className="w-full max-w-md bg-surface-container-lowest rounded-3xl border border-hairline p-6 sm:p-10 shadow-soft relative overflow-hidden mx-auto">
          <div className="absolute -top-20 -left-20 w-48 h-48 bg-tertiary-fixed opacity-70 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute top-4 right-4 z-20">
            <LanguageSwitcher />
          </div>
          <div className="relative z-10">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-surface-container-high mb-6 shadow-sm">
                <span className="material-symbols-outlined filled text-4xl text-primary">
                  school
                </span>
              </div>
              <h1 className="font-headline-xl text-headline-xl text-primary mb-2">
                {t('auth.createTitle')}
              </h1>
              <p className="font-body-md text-body-md text-on-surface-variant">
                {t('auth.createSubtitle')}
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <input
                ref={honeypotRef}
                type="text"
                name="website"
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                className="sr-only"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <TextField
                    icon="person"
                    placeholder={t('auth.firstName')}
                    type="text"
                    {...register('firstName')}
                  />
                  {errors.firstName && (
                    <p className="text-error text-body-sm mt-2">{errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <TextField
                    placeholder={t('auth.lastName')}
                    type="text"
                    {...register('lastName')}
                  />
                  {errors.lastName && (
                    <p className="text-error text-body-sm mt-2">{errors.lastName.message}</p>
                  )}
                </div>
              </div>
              <div>
                <TextField
                  icon="alternate_email"
                  placeholder={t('auth.email')}
                  type="email"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-error text-body-sm mt-2">{errors.email.message}</p>
                )}
              </div>
              {/* RG180, oubay c2f3b5a: phone and gender, asked with omar's consent below. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <TextField
                    icon="call"
                    placeholder={t('auth.phone')}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    {...register('phone')}
                  />
                  {errors.phone && (
                    <p className="text-error text-body-sm mt-2">{errors.phone.message}</p>
                  )}
                </div>
                <div>
                  <select
                    aria-label={t('auth.gender')}
                    defaultValue=""
                    className="w-full min-h-[48px] rounded-2xl border border-outline-variant bg-surface-container-lowest px-4 text-body-md text-on-surface focus:outline-none focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/30"
                    {...register('gender')}
                  >
                    <option value="" disabled>
                      {t('auth.gender')}
                    </option>
                    <option value="MALE">{t('auth.genderMale')}</option>
                    <option value="FEMALE">{t('auth.genderFemale')}</option>
                  </select>
                  {errors.gender && (
                    <p className="text-error text-body-sm mt-2">{errors.gender.message}</p>
                  )}
                </div>
              </div>

              {/* Password field */}
              <div>
                <TextField
                  icon="lock"
                  placeholder={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  trailing={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="p-1 text-on-surface-variant hover:text-primary rounded-full hover:bg-surface-container-high transition-colors focus:outline-none"
                      title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    >
                      <span className="material-symbols-outlined text-[20px] block">
                        {showPassword ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  }
                />
                {errors.password && (
                  <p className="text-error text-body-sm mt-2">{errors.password.message}</p>
                )}

                {/* Password Strength Meter */}
                {watchedPassword && (
                  <div className="mt-2.5 space-y-1.5 animate-fadeIn">
                    <div className="flex items-center justify-between font-label-sm text-label-sm">
                      <span className="text-on-surface-variant">{t('auth.passwordStrength')}</span>
                      {strength.labelKey && (
                        <span className={`font-semibold ${strength.colorClass}`}>
                          {t(strength.labelKey)}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1.5 h-1.5 w-full">
                      <div
                        className={`flex-1 rounded-full transition-all duration-300 ${
                          strength.score >= 1 ? strength.barColor : 'bg-surface-container-high'
                        }`}
                      />
                      <div
                        className={`flex-1 rounded-full transition-all duration-300 ${
                          strength.score >= 2 ? strength.barColor : 'bg-surface-container-high'
                        }`}
                      />
                      <div
                        className={`flex-1 rounded-full transition-all duration-300 ${
                          strength.score >= 3 ? strength.barColor : 'bg-surface-container-high'
                        }`}
                      />
                    </div>
                  </div>
                )}

                <p className="font-label-sm text-label-sm text-on-surface-variant mt-2">
                  {t('auth.passwordRequirements')}
                </p>
              </div>

              {/* Confirm Password field */}
              <div>
                <TextField
                  icon="lock_reset"
                  placeholder={t('auth.confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  {...register('confirmPassword')}
                  trailing={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="p-1 text-on-surface-variant hover:text-primary rounded-full hover:bg-surface-container-high transition-colors focus:outline-none"
                      title={showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                      aria-label={
                        showConfirmPassword ? t('auth.hidePassword') : t('auth.showPassword')
                      }
                    >
                      <span className="material-symbols-outlined text-[20px] block">
                        {showConfirmPassword ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  }
                />
                {errors.confirmPassword && (
                  <p className="text-error text-body-sm mt-2">{errors.confirmPassword.message}</p>
                )}
              </div>

              <div>
                <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-5 w-5 shrink-0 accent-primary rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                    {...register('acceptsPrivacyPolicy')}
                  />
                  <span className="font-body-sm text-body-sm text-on-surface-variant leading-snug">
                    {t('auth.privacyPolicyConsent')}{' '}
                    <Link
                      to="/privacy"
                      className="font-title-md text-body-sm text-primary hover:underline underline-offset-4"
                    >
                      {t('auth.privacyPolicyLink')}
                    </Link>
                  </span>
                </label>
                {errors.acceptsPrivacyPolicy && (
                  <p className="text-error text-body-sm mt-2">{t('auth.privacyPolicyRequired')}</p>
                )}
              </div>

              {error && <p className="text-error text-body-md text-center">{error}</p>}

              <Button type="submit" className="w-full" disabled={submitting}>
                {t('auth.signUp')}
              </Button>
            </form>

            <div className="mt-8 text-center">
              <p className="font-body-md text-body-md text-on-surface-variant">
                {t('auth.alreadyHave')}{' '}
                <Link
                  to="/login"
                  className="font-title-md text-body-md text-primary hover:underline underline-offset-4 ml-1"
                >
                  {t('auth.signIn')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
